import React, { useState } from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuGroupHeader,
  MenuDivider,
  Spinner,
} from "@fluentui/react-components";
import { Folder24Regular } from "@fluentui/react-icons";
import type { FolderSuggestion } from "../suggestions";
import type { MailFolder } from "../mailbox-adapter";

interface FolderSuggestButtonProps {
  disabled: boolean;
  disabledReason?: string;
  className: string;
  iconClassName: string;
  labelClassName: string;
  /** Fetches fresh AI suggestions + the full existing folder list. Called every
   * time the menu opens (never cached) so a different open email always gets
   * its own suggestions. */
  onSuggest: () => Promise<{ suggestions: FolderSuggestion[]; existing: MailFolder[] }>;
  /** Fires once per pick; the menu closes immediately (single-select, no
   * confirmation — matches the app's other non-destructive quick actions). */
  onSelect: (folderName: string) => void;
}

export function FolderSuggestButton({
  disabled,
  disabledReason,
  className,
  iconClassName,
  labelClassName,
  onSuggest,
  onSelect,
}: FolderSuggestButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<FolderSuggestion[]>([]);
  const [existing, setExisting] = useState<MailFolder[]>([]);

  async function handleOpenChange(_event: unknown, data: { open: boolean }) {
    setOpen(data.open);
    if (!data.open) {
      return;
    }
    setLoading(true);
    setSuggestions([]);
    setExisting([]);
    // onSuggest is expected to catch its own errors and resolve with an empty
    // result — this catch/finally is a last-resort guard so a stray rejection
    // can never leave the menu spinning forever or surface as an unhandled
    // promise rejection (defense in depth, not the primary fix).
    try {
      const result = await onSuggest();
      setSuggestions(result.suggestions);
      setExisting(result.existing);
    } catch {
      setSuggestions([]);
      setExisting([]);
    } finally {
      setLoading(false);
    }
  }

  const suggestedNames = new Set(suggestions.map((s) => s.name.toLowerCase()));
  const otherFolders = existing.filter((f) => !suggestedNames.has(f.displayName.toLowerCase()));

  return (
    <Menu open={open} onOpenChange={(e, data) => void handleOpenChange(e, data)}>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={className}
          disabled={disabled}
          title={disabled ? disabledReason : "Suggest Folder"}
        >
          <Folder24Regular className={iconClassName} aria-hidden />
          <span className={labelClassName}>Suggest Folder</span>
        </button>
      </MenuTrigger>
      <MenuPopover>
        {loading ? (
          <div style={{ padding: 12 }}>
            <Spinner size="tiny" label="Thinking…" />
          </div>
        ) : (
          <MenuList>
            {suggestions.length === 0 && otherFolders.length === 0 ? (
              <MenuItem disabled>No folders found</MenuItem>
            ) : (
              <>
                {suggestions.length > 0 && (
                  <>
                    <MenuGroupHeader>Suggested</MenuGroupHeader>
                    {suggestions.map((s) => (
                      <MenuItem
                        key={s.name}
                        secondaryContent={s.reason}
                        onClick={() => onSelect(s.name)}
                      >
                        {s.name}
                      </MenuItem>
                    ))}
                  </>
                )}
                {otherFolders.length > 0 && (
                  <>
                    {suggestions.length > 0 && <MenuDivider />}
                    <MenuGroupHeader>All folders</MenuGroupHeader>
                    {otherFolders.map((f) => (
                      <MenuItem key={f.id} onClick={() => onSelect(f.displayName)}>
                        {f.displayName}
                      </MenuItem>
                    ))}
                  </>
                )}
              </>
            )}
          </MenuList>
        )}
      </MenuPopover>
    </Menu>
  );
}
